import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Input } from "semantic-ui-react";

import { DATE_FORMATS_FNS, setSelectedLocation } from "components";
import { DealersAndLocationsDropdownKeys } from "components/DealersAndLocationsDropdown/queryKeys";
import "components/UploadImage/UploadImage.scss";
import { typedQueryClient } from "hooks";
import { UploadFilePreview } from "lib/Form/components/FormUploadImage/UploadFilePreview";
import { useUploadCompanyStampMutation, useUploadLogoMutation } from "modules/LocationOverviewDashboard/components/General/components/ImageUpload/hooks";
import { clickOnId } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { LocationDetailFormPayload } from "modules/LocationOverviewDashboard/hooks";

type ImageUploadProps = {
  fieldName: "logo" | "company_stamp";
  label: string;
  inputId: string;
};

export const updateLocationImageCache = (field: "logo" | "company_stamp", value: string) => {
  const selectedLocation = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.selectedLocation);
  if (!selectedLocation) return;

  const dealersList = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.dealers);
  const dealerIdx = dealersList?.dealers.findIndex(dealer => dealer.id === selectedLocation.dealer_id) ?? -1;

  if (dealersList && dealerIdx !== -1) {
    const dealer = dealersList.dealers[dealerIdx];
    const locationIdx = dealer.locations?.findIndex(location => location.id === selectedLocation.id) ?? -1;

    if (dealer.locations && locationIdx !== -1)
      typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.dealers, {
        ...dealersList,
        dealers: dealersList.dealers.with(dealerIdx, {
          ...dealer,
          locations: dealer.locations.with(locationIdx, { ...dealer.locations[locationIdx], [field]: value })
        })
      });
  }

  setSelectedLocation({ ...selectedLocation, [field]: value });
};

export const ImageUpload = ({ fieldName, label, inputId }: ImageUploadProps) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<LocationDetailFormPayload>();

  const { mutate: uploadLogo } = useUploadLogoMutation();
  const { mutate: uploadCompanyStamp } = useUploadCompanyStampMutation();

  const [locationName, image] = watch(["name", fieldName]);
  const fileName = image ? (image.split("/").pop()?.split("?")[0] ?? "") : "";

  const handleChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (fieldName === "company_stamp") {
      uploadCompanyStamp({ path: `${locationName}/${formatDate(new Date(), DATE_FORMATS_FNS.yearMonthDateSlash)}`, file });
      return;
    }

    uploadLogo(file);
  };

  const handleDelete = () => {
    updateLocationImageCache(fieldName, "");
    setValue(fieldName, "");
  };

  return (
    <div className="upload-container">
      <div className="UploadImage new">
        <div className="button" onClick={() => clickOnId(inputId)}>
          <p className="btn-text">{t("v8_choose_file", "Choose File")}</p>
        </div>
        <p className="fileInput-ellipsis">{fileName || label}</p>
      </div>
      <Input id={inputId} className="hidden-file-input" accept="image/png, image/jpeg, image/jpg" type="file" onChange={handleChangeFile} />
      <UploadFilePreview image={image} onDelete={handleDelete} />
    </div>
  );
};
